import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { X, ChevronRight, Search, DatabaseZap, Info } from "lucide-react";
import { isSentinel, humanize } from "./api";
import { ARI_STATUS_DESC, ARI_VALUE_AUTHORITY_DESC } from "./ari";

export { humanize };

/* ---------------- contexts ---------------- */
export const ContractCtx = createContext(null);
export const useModel = () => useContext(ContractCtx);
export const AriCtx = createContext(null);
export const useAri = () => useContext(AriCtx);
export const SelectionCtx = createContext(null);
export const useSelection = () => useContext(SelectionCtx);
export const TaxonomyCtx = createContext(null);
export const useTaxonomy = () => useContext(TaxonomyCtx);

/* ---------------- status styling ---------------- */
export const STATUS_COLORS = {
  PASS: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  FAIL: "bg-rose-50 text-rose-700 ring-rose-600/20",
  RUNNING: "bg-sky-50 text-sky-700 ring-sky-600/20",
  BLOCKED: "bg-amber-50 text-amber-800 ring-amber-600/20",
  NOT_RUN: "bg-stone-100 text-stone-600 ring-stone-500/20",
  NOT_PROVEN: "bg-amber-50 text-amber-800 ring-amber-600/20",
  NOT_CAPTURED: "bg-stone-100 text-stone-500 ring-stone-400/20",
  STALE: "bg-amber-50 text-amber-800 ring-amber-600/20",
  INVALID: "bg-rose-50 text-rose-700 ring-rose-600/20",
  FINANCIAL_ELIGIBLE: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  UNCLASSIFIED: "bg-stone-100 text-stone-600 ring-stone-500/20",
  // ari governed fact statuses
  CERTIFIED: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  EXTRACTED_RAW: "bg-sky-50 text-sky-700 ring-sky-600/20",
  RESOLVED_PROVISIONAL: "bg-indigo-50 text-indigo-700 ring-indigo-600/20",
  MAPPED_PROVISIONAL: "bg-violet-50 text-violet-700 ring-violet-600/20",
  SOURCE_CANDIDATE: "bg-stone-100 text-stone-600 ring-stone-500/20",
  REVIEW_REQUIRED: "bg-amber-50 text-amber-800 ring-amber-600/20",
  PAGE_PROVEN_ATOM_PENDING: "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-600/20",
  // value authority
  AUDITED_ACTUAL: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  REPORTED_ACTUAL: "bg-sky-50 text-sky-700 ring-sky-600/20",
  MANAGEMENT_ESTIMATE: "bg-amber-50 text-amber-800 ring-amber-600/20",
  MANAGEMENT_GUIDANCE: "bg-indigo-50 text-indigo-700 ring-indigo-600/20",
  AI_DERIVED_INFERENCE: "bg-rose-50 text-rose-700 ring-rose-600/20",
};

export const StatusBadge = ({ value }) => {
  const key = String(value ?? "").toUpperCase();
  const cls = STATUS_COLORS[key];
  const desc = ARI_STATUS_DESC[key] || ARI_VALUE_AUTHORITY_DESC[key];
  if (!cls) return <span className="text-slate-700">{value ?? "—"}</span>;
  return (
    <span title={desc || ""} data-testid={`status-${key}`}
      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-bold ring-1 ring-inset ${cls}`}>{key}</span>
  );
};

export const KeyBadge = ({ kind }) => (
  <span className={`mr-1 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-extrabold ${kind === "PK" ? "bg-indigo-50 text-indigo-700" : "bg-orange-50 text-orange-700"}`}>{kind}</span>
);

export const Panel = ({ children, className = "", ...rest }) => (
  <div className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm ${className}`} {...rest}>{children}</div>
);

export const SectionHead = ({ eyebrow, title, caption, right }) => (
  <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
    <div>
      {eyebrow && <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{eyebrow}</div>}
      <h2 className="text-2xl font-extrabold text-slate-900">{title}</h2>
      {caption && <p className="mt-1 max-w-3xl text-sm text-slate-500">{caption}</p>}
    </div>
    {right}
  </div>
);

export const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1"><span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</span>{children}</label>
);

export const cellVal = (v) => (isSentinel(v) ? <em className="text-slate-400">{String(v ?? "NULL")}</em> : String(v));

export const Row = ({ k, v }) => (
  <div className="flex justify-between gap-2"><dt className="text-slate-400">{k}</dt><dd className="text-right font-medium text-slate-700">{v}</dd></div>
);

/* ---------------- Drawer ---------------- */
export const Drawer = ({ open, onClose, title, children }) =>
  !open ? null : (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="drawer">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <div className="relative h-full w-[540px] overflow-auto bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-extrabold text-slate-900">{title}</h3>
          <button data-testid="drawer-close" onClick={onClose} className="rounded p-1 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );

/* ---------------- Source-evidence drawer (universal fact provenance) ---------------- */
export const SourceEvidenceDrawer = ({ open, onClose, fact }) => (
  <Drawer open={open} onClose={onClose} title="Source evidence">
    {fact && (
      <div className="space-y-3 text-sm" data-testid="source-evidence">
        <div className="rounded-lg bg-slate-50 p-3">
          <div className="font-bold text-slate-800">{fact.label || fact.raw_text || "—"}</div>
          {fact.value != null && <div className="mt-1 font-mono text-slate-700">{String(fact.value)}</div>}
        </div>
        <dl className="space-y-1.5">
          <Row k="Company" v={cellVal(fact.company)} />
          <Row k="Report / FY" v={cellVal(fact.report || fact.financial_year)} />
          <Row k="Page" v={cellVal(fact.page)} />
          <Row k="Confidence" v={cellVal(fact.confidence)} />
          <div className="flex justify-between gap-2"><dt className="text-slate-400">Extraction status</dt><dd><StatusBadge value={fact.status || "EXTRACTED_RAW"} /></dd></div>
          <div className="flex justify-between gap-2"><dt className="text-slate-400">Review status</dt><dd><StatusBadge value={fact.review_status || "PAGE_PROVEN_ATOM_PENDING"} /></dd></div>
        </dl>
        <div>
          <div className="mb-1 font-bold text-slate-700">Exact source text</div>
          <div className="rounded border border-slate-200 bg-white p-2 font-mono text-xs text-slate-700">{fact.source_text || fact.raw_text || <span className="text-slate-400">Not captured</span>}</div>
        </div>
        <div>
          <div className="mb-1 font-bold text-slate-700">Bounding box</div>
          {fact.bbox || (fact.x0 != null) ? (
            <div className="font-mono text-xs text-slate-600">x0 {cellVal(fact.x0)} · y0 {cellVal(fact.y0)} · x1 {cellVal(fact.x1)} · y1 {cellVal(fact.y1)}</div>
          ) : <div className="text-xs text-slate-400">Coordinates not available for this fact.</div>}
        </div>
      </div>
    )}
  </Drawer>
);

/* ---------------- generic searchable/paginated table ---------------- */
export function DataTable({ records, testid, onRow, columns, pageSize = 10 }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const cols = useMemo(() => columns || Array.from(new Set(records.slice(0, 40).flatMap((r) => Object.keys(r)))), [records, columns]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? records.filter((r) => Object.values(r).some((v) => String(v ?? "").toLowerCase().includes(s))) : records;
  }, [records, q]);
  useEffect(() => setPage(0), [q, records]);
  const paged = filtered.slice(page * pageSize, page * pageSize + pageSize);
  const pages = Math.ceil(filtered.length / pageSize) || 1;
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-2 h-4 w-4 text-slate-400" />
          <input data-testid={`${testid}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
            className="w-72 rounded-lg border border-slate-200 py-1.5 pl-8 pr-3 text-sm outline-none focus:border-indigo-400" />
        </div>
        <span className="text-xs text-slate-400">{filtered.length} records</span>
      </div>
      <div className="overflow-auto rounded-lg border border-slate-200" style={{ maxHeight: 560 }}>
        <table className="w-full border-collapse text-sm" data-testid={testid}>
          <thead className="sticky top-0 bg-slate-50">
            <tr>{cols.map((c) => <th key={c} className="whitespace-nowrap border-b border-slate-200 px-3 py-2 text-left font-bold text-slate-700">{humanize(c)}<div className="font-mono text-[10px] font-medium text-slate-400">{c}</div></th>)}</tr>
          </thead>
          <tbody>
            {paged.map((r, i) => (
              <tr key={r.id || i} data-testid={`${testid}-row`} onClick={() => onRow?.(r)} className={`border-b border-slate-100 ${onRow ? "cursor-pointer hover:bg-indigo-50/40" : ""}`}>
                {cols.map((c) => {
                  const up = String(r[c] ?? "").toUpperCase();
                  return <td key={c} className="max-w-xs truncate px-3 py-1.5 text-slate-700">{STATUS_COLORS[up] ? <StatusBadge value={up} /> : cellVal(r[c])}</td>;
                })}
              </tr>
            ))}
            {paged.length === 0 && <tr><td colSpan={cols.length} className="px-3 py-6 text-center text-slate-400">No records match.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mt-2 flex items-center justify-end gap-2 text-sm">
        <button className="rounded border border-slate-200 px-2 py-1 disabled:opacity-40" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Prev</button>
        <span className="text-slate-500">Page {page + 1} / {pages}</span>
        <button className="rounded border border-slate-200 px-2 py-1 disabled:opacity-40" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>Next</button>
      </div>
    </div>
  );
}

/* ---------------- Awaiting governed source (no synthetic data) ---------------- */
export function AwaitingSource({ tables = [], columns = [], caption, testid = "awaiting-source" }) {
  const avail = useAri()?.availability || {};
  return (
    <Panel data-testid={testid} className="border-dashed">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-amber-50 p-2 text-amber-700"><DatabaseZap className="h-5 w-5" /></div>
        <div className="flex-1">
          <div className="font-extrabold text-slate-800">Awaiting governed source</div>
          <p className="mt-1 text-sm text-slate-500">
            {caption || "This view is powered by the ari_source schema, which has not yet been delivered in the governed handoff. No synthetic data is shown — real records will appear automatically the moment the governed export or SQL API is available."}
          </p>
          {tables.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {tables.map((t) => (
                <span key={t} data-testid={`feed-${t}`} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 font-mono text-[11px] text-slate-600">
                  {t}
                  <span className={`h-1.5 w-1.5 rounded-full ${avail[t]?.available ? "bg-emerald-500" : "bg-slate-300"}`} />
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      {columns.length > 0 && (
        <div className="mt-4 overflow-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50"><tr>{columns.map((c) => <th key={c} className="px-3 py-2 text-left font-bold text-slate-600">{c}</th>)}</tr></thead>
            <tbody><tr><td colSpan={columns.length} className="px-3 py-8 text-center text-slate-400">No governed records yet — awaiting ari_source delivery.</td></tr></tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

/* ---------------- Candidate vs resolved counters ---------------- */
export const CandidateResolved = ({ candidate = 0, resolved = 0 }) => (
  <div className="flex gap-2" data-testid="candidate-resolved">
    <span className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2.5 py-1 text-xs font-semibold text-stone-600">Candidates <b>{candidate}</b></span>
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">Resolved <b>{resolved}</b></span>
  </div>
);

/* ---------------- status legend ---------------- */
export function StatusLegend({ statuses, title = "Data status" }) {
  return (
    <Panel data-testid="status-legend">
      <div className="mb-2 flex items-center gap-2 font-bold text-slate-800"><Info className="h-4 w-4 text-indigo-600" />{title}</div>
      <div className="flex flex-wrap gap-2">
        {statuses.map((s) => (
          <div key={s} className="flex items-center gap-2" title={ARI_STATUS_DESC[s] || ARI_VALUE_AUTHORITY_DESC[s] || ""}>
            <StatusBadge value={s} />
          </div>
        ))}
      </div>
    </Panel>
  );
}

export { ChevronRight };
